import React, { useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import { TemplateProps, createTemplateUtils } from "../BaseTemplate";
import PublicPlanSummary from "../PublicPlanSummary";
import PublicFinancialSummary from "../PublicFinancialSummary";
import PublicOptionalItemsSummary from "../PublicOptionalItemsSummary";
import OfferFilesSection from "../OfferFilesSection";
import { analyticsService } from "../../../services/analyticsService";
import { CalendarIcon, UsersIcon } from "../../icons";
import { getFormattedOfferValidityDate } from "../utils/offerUtils";

const ModernTemplate: React.FC<TemplateProps> = ({
  offer,
  brandKit,
  termsAndConditions,
  editMode = false,
  onColorChange,
  showColorPicker,
  setShowColorPicker,
  onHeroUpload,
  onHeroRemove,
  uploadingHero = false,
  onTermsToggle,
  onTermsContentChange,
}) => {
  const utils = createTemplateUtils(brandKit);
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top: number;
    left: number;
  } | null>(null);

  const handleClick = (e: React.MouseEvent) => {
    if (!editMode && analyticsService.isSessionActive()) {
      const rect = e.currentTarget.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      analyticsService.trackClick(
        (e.target as HTMLElement).tagName.toLowerCase(),
        x,
        y,
        "offer-page"
      );
    }
  };

  const calculateDropdownPosition = useCallback(
    (buttonElement: HTMLButtonElement): { top: number; left: number } => {
      const rect = buttonElement.getBoundingClientRect();
      const dropdownWidth = 256; // Approximate width for color picker popup
      const dropdownHeight = 120; // Approximate height for color picker popup

      let left = rect.right - dropdownWidth;
      let top = rect.bottom + 8; // mt-2 = 8px

      // Check if dropdown would go off the right edge
      if (left < 10) {
        left = rect.left;
      }

      // Check if dropdown would go off the bottom edge
      if (top + dropdownHeight > window.innerHeight - 10) {
        top = rect.top - dropdownHeight - 8;
      }

      // Ensure minimum distances from viewport edges
      left = Math.max(
        10,
        Math.min(left, window.innerWidth - dropdownWidth - 10)
      );
      top = Math.max(10, top);

      return { top, left };
    },
    []
  );

  const handleColorPickerClick = useCallback(
    (colorType: string) => {
      if (showColorPicker === colorType) {
        setShowColorPicker?.(null);
        setDropdownPosition(null);
      } else {
        const buttonElement = buttonRefs.current[colorType];
        if (buttonElement) {
          const position = calculateDropdownPosition(buttonElement);
          setDropdownPosition(position);
          setShowColorPicker?.(colorType);
        }
      }
    },
    [showColorPicker, setShowColorPicker, calculateDropdownPosition]
  );

  // Close dropdown when clicking outside or on scroll
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;

      // Check if click is on a dropdown button
      const isDropdownButton = Object.values(buttonRefs.current).some(
        (button) => button && button.contains(target)
      );

      // Check if click is inside the portal dropdown
      const isInsideDropdown = document
        .getElementById("color-picker-portal")
        ?.contains(target);

      if (!isDropdownButton && !isInsideDropdown) {
        setShowColorPicker?.(null);
        setDropdownPosition(null);
      }
    };

    const handleScroll = () => {
      if (showColorPicker) {
        setShowColorPicker?.(null);
        setDropdownPosition(null);
      }
    };

    if (showColorPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleScroll);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [showColorPicker, setShowColorPicker]);

  const ColorPickerButton: React.FC<{
    colorType: string;
    color: string;
    label: string;
  }> = ({ colorType, color, label }) => (
    <button
      ref={(el) => {
        buttonRefs.current[colorType] = el;
      }}
      onClick={() => handleColorPickerClick(colorType)}
      className="w-8 h-8 rounded-full border-2 border-white shadow-md hover:scale-110 transition-transform"
      style={{ backgroundColor: color }}
      title={label}
    />
  );

  return (
    <div
      className="font-sans"
      onClick={handleClick}
      style={
        {
          "--primary-color": utils.colors.primary || "#3B82F6",
          "--secondary-color": utils.colors.secondary || "#6B7280",
          "--accent-color": utils.colors.accent || "#10B981",
          "--gradient-start": utils.colors.gradientStart || "#334155",
          "--gradient-end": utils.colors.gradientEnd || "#1e293b",
          "--hotel-info-bg": utils.colors.hotelInfo || "#10B981",
          "--hotel-info-background":
            utils.colors.hotelInfoBackground || "#10B981",
          "--day-background": utils.colors.dayBackground || "#f1f5f9",
          "--day-background-end": utils.colors.dayBackgroundEnd || "#e2e8f0",
          "--header-color": utils.colors.headerColor || "#334155",
        } as React.CSSProperties
      }
    >
      <div className="max-w-4xl mx-auto">
        {/* Unified Offer Card - Key Visual, Header, and Details */}
        <div className="bg-white rounded-xl border border-slate-200 mb-8 overflow-hidden">
          {/* Key Visual Section - only show if exists or in edit mode */}
          {(utils.keyVisualUrl || editMode) && (
            <div className="relative group">
              {utils.keyVisualUrl ? (
                <div className="relative">
                  <img
                    src={utils.keyVisualUrl}
                    alt="Key Visual"
                    className="w-full"
                  />
                  {editMode && (
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-black group-hover:bg-opacity-40 transition-all duration-200 flex items-center justify-center">
                      <div className="hidden group-hover:flex space-x-4">
                        <button
                          onClick={() => {
                            const input = document.createElement("input");
                            input.type = "file";
                            input.accept = "image/jpeg,image/png,image/webp";
                            input.onchange = (e) => {
                              const file = (e.target as HTMLInputElement)
                                .files?.[0];
                              if (file) onHeroUpload?.(file);
                            };
                            input.click();
                          }}
                          className="px-4 py-2 bg-white text-neutral-900 rounded-lg hover:bg-neutral-100 transition-colors font-medium"
                          disabled={uploadingHero}
                        >
                          {uploadingHero ? "Uploading..." : "Podmień"}
                        </button>
                        <button
                          onClick={onHeroRemove}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
                        >
                          Usuń
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : editMode ? (
                <div className="h-32 border-2 border-dashed border-neutral-300 flex items-center justify-center">
                  <button
                    onClick={() => {
                      const input = document.createElement("input");
                      input.type = "file";
                      input.accept = "image/jpeg,image/png,image/webp";
                      input.onchange = (e) => {
                        const file = (e.target as HTMLInputElement).files?.[0];
                        if (file) onHeroUpload?.(file);
                      };
                      input.click();
                    }}
                    className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    disabled={uploadingHero}
                  >
                    {uploadingHero ? "Uploading..." : "Dodaj Key Visual"}
                  </button>
                </div>
              ) : null}
            </div>
          )}

          {/* Modern Professional Offer Header with gradients */}
          <div
            className="px-8 py-6 text-white relative"
            style={{
              background: `linear-gradient(135deg, ${utils.colors.gradientStart || "#334155"} 0%, ${utils.colors.gradientEnd || "#1e293b"} 100%)`,
            }}
          >
            <div className="flex justify-between items-start relative z-10">
              <div>
                <h1 className="text-3xl font-bold mb-2">{offer.title}</h1>
                <p className="text-slate-200">
                  Przygotowane dla: {offer.clientName}
                </p>
              </div>
              {editMode && (
                <div className="absolute top-2 right-2 flex gap-2 print:hidden">
                  <ColorPickerButton
                    colorType="gradientStart"
                    color={utils.colors.gradientStart || "#334155"}
                    label="Kolor gradientu (początek)"
                  />
                  <ColorPickerButton
                    colorType="gradientEnd"
                    color={utils.colors.gradientEnd || "#1e293b"}
                    label="Kolor gradientu (koniec)"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Hotel Info Bar with color picker */}
          <div
            className="px-8 py-4 border-b border-slate-200 relative group"
            style={{
              backgroundColor: utils.colors.hotelInfoBackground || "#f1f5f9",
            }}
          >
            {editMode && (
              <div className="absolute top-2 right-2 print:hidden">
                <ColorPickerButton
                  colorType="hotelInfoBackground"
                  color={utils.colors.hotelInfoBackground || "#f1f5f9"}
                  label="Tło sekcji hotelu"
                />
              </div>
            )}
            <div className="flex justify-between items-center text-sm">
              <div className="font-semibold text-slate-800">
                {offer.hotel.name}
              </div>
              <div className="text-slate-600">
                {utils.formatDate(new Date().toISOString())}
              </div>
            </div>
          </div>

          {/* Stay Details Section - Improved single-row layout */}
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-6">
              {/* Dates - 2/3 width */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200 flex-[2]">
                <CalendarIcon className="w-5 h-5 text-slate-600 flex-shrink-0" />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">
                    Termin pobytu:
                  </span>
                  <span className="text-base font-semibold text-slate-900">
                    {utils.formatDate(offer.checkInDate)} -{" "}
                    {utils.formatDate(offer.checkOutDate)}
                  </span>
                </div>
              </div>

              {/* Guests - 1/3 width */}
              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-lg border border-slate-200 flex-[1]">
                <UsersIcon className="w-5 h-5 text-slate-600 flex-shrink-0" />
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-slate-700">
                    Liczba osób:
                  </span>
                  <span className="text-base font-semibold text-slate-900">
                    {offer.numberOfGuests}
                  </span>
                </div>
              </div>
            </div>

            {/* Description Section */}
            {offer.description && (
              <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200 text-left">
                <div className="text-sm font-medium text-slate-700 mb-2">
                  Jak zrozumieliśmy Państwa sytuację:
                </div>
                <div className="text-base text-slate-900 leading-relaxed">
                  {offer.description}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Separate sections for plan and financial summary */}
        <div className="space-y-6 mb-8">
          <div className="relative">
            <PublicPlanSummary offer={offer} />
            {editMode && (
              <div className="absolute top-2 right-2 flex gap-2 print:hidden">
                <ColorPickerButton
                  colorType="headerColor"
                  color={utils.colors.headerColor || "#334155"}
                  label="Kolor nagłówków"
                />
                <ColorPickerButton
                  colorType="dayBackground"
                  color={utils.colors.dayBackground || "#f1f5f9"}
                  label="Tło dni (początek)"
                />
                <ColorPickerButton
                  colorType="dayBackgroundEnd"
                  color={utils.colors.dayBackgroundEnd || "#e2e8f0"}
                  label="Tło dni (koniec)"
                />
              </div>
            )}
          </div>
          {offer.financialSummary && (
            <PublicFinancialSummary financialSummary={offer.financialSummary} />
          )}
          {offer.optionalItems && offer.optionalItems.length > 0 && (
            <PublicOptionalItemsSummary optionalItems={offer.optionalItems} />
          )}
        </div>

        {/* Files Section */}
        {offer.offerFiles && offer.offerFiles.length > 0 && (
          <div className="mb-8 print:hidden">
            <OfferFilesSection
              offerFiles={offer.offerFiles}
              offerToken={offer.publicToken || offer.id}
            />
          </div>
        )}

        {/* Terms and Conditions Section */}
        {termsAndConditions?.enabled ? (
          <div className="relative group border border-neutral-200 rounded-lg p-6 mb-8 bg-gradient-to-r from-slate-50 to-blue-50 text-left">
            {editMode && (
              <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                <button
                  onClick={() => onTermsToggle?.(false)}
                  className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                >
                  Usuń
                </button>
              </div>
            )}

            <h3 className="text-xl font-semibold mb-3 text-neutral-900">
              Warunki oferty
            </h3>

            {editMode ? (
              <textarea
                value={termsAndConditions.content || ""}
                onChange={(e) => onTermsContentChange?.(e.target.value)}
                placeholder="Wprowadź warunki oferty..."
                className="w-full h-24 p-3 border border-neutral-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            ) : (
              <div
                className="text-neutral-700"
                dangerouslySetInnerHTML={{
                  __html:
                    termsAndConditions.content?.replace(/\n/g, "<br>") || "",
                }}
              />
            )}
          </div>
        ) : editMode ? (
          <div className="border-2 border-dashed border-neutral-300 rounded-lg p-6 text-center mb-8">
            <button
              onClick={() => onTermsToggle?.(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Dodaj sekcję "Warunki oferty"
            </button>
          </div>
        ) : null}

        {/* Footer */}
        <footer className="text-center text-sm text-neutral-500 mt-12">
          <p>
            Ta oferta jest ważna do{" "}
            {getFormattedOfferValidityDate(offer, utils)}
          </p>
        </footer>
      </div>

      {/* Color Picker Portal */}
      {showColorPicker &&
        dropdownPosition &&
        createPortal(
          <div
            id="color-picker-portal"
            style={{
              position: "fixed",
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              zIndex: 9999,
            }}
            className="bg-white border border-gray-200 rounded-lg p-4 shadow-lg"
          >
            <label className="block text-xs font-medium mb-2 text-slate-950">
              {showColorPicker === "gradientStart"
                ? "Kolor gradientu (początek)"
                : showColorPicker === "gradientEnd"
                  ? "Kolor gradientu (koniec)"
                  : showColorPicker === "primary"
                    ? "Kolor główny"
                    : showColorPicker === "secondary"
                      ? "Kolor drugoplanowy"
                      : showColorPicker === "accent"
                        ? "Kolor akcentu"
                        : showColorPicker === "hotelInfo"
                          ? "Kolor sekcji hotelu"
                          : showColorPicker === "hotelInfoBackground"
                            ? "Tło sekcji hotelu"
                            : showColorPicker === "dayBackground"
                              ? "Tło dni (początek)"
                              : showColorPicker === "dayBackgroundEnd"
                                ? "Tło dni (koniec)"
                                : showColorPicker === "headerColor"
                                  ? "Kolor nagłówków"
                                  : "Kolor"}
            </label>
            <div className="space-y-3">
              <input
                type="color"
                value={
                  showColorPicker === "gradientStart"
                    ? utils.colors.gradientStart || "#334155"
                    : showColorPicker === "gradientEnd"
                      ? utils.colors.gradientEnd || "#1e293b"
                      : showColorPicker === "primary"
                        ? utils.colors.primary || "#3B82F6"
                        : showColorPicker === "secondary"
                          ? utils.colors.secondary || "#6B7280"
                          : showColorPicker === "accent"
                            ? utils.colors.accent || "#10B981"
                            : showColorPicker === "hotelInfo"
                              ? utils.colors.hotelInfo || "#10B981"
                              : showColorPicker === "hotelInfoBackground"
                                ? utils.colors.hotelInfoBackground || "#f1f5f9"
                                : showColorPicker === "dayBackground"
                                  ? utils.colors.dayBackground || "#f1f5f9"
                                  : showColorPicker === "dayBackgroundEnd"
                                    ? utils.colors.dayBackgroundEnd || "#e2e8f0"
                                    : showColorPicker === "headerColor"
                                      ? utils.colors.headerColor || "#334155"
                                      : "#000000"
                }
                onChange={(e) =>
                  onColorChange?.(showColorPicker, e.target.value)
                }
                className="w-full h-10 rounded cursor-pointer"
              />
              <div>
                <label className="block text-xs text-slate-600 mb-1">
                  Lub wklej kod HEX:
                </label>
                <input
                  type="text"
                  value={
                    showColorPicker === "gradientStart"
                      ? utils.colors.gradientStart || "#334155"
                      : showColorPicker === "gradientEnd"
                        ? utils.colors.gradientEnd || "#1e293b"
                        : showColorPicker === "primary"
                          ? utils.colors.primary || "#3B82F6"
                          : showColorPicker === "secondary"
                            ? utils.colors.secondary || "#6B7280"
                            : showColorPicker === "accent"
                              ? utils.colors.accent || "#10B981"
                              : showColorPicker === "hotelInfo"
                                ? utils.colors.hotelInfo || "#10B981"
                                : showColorPicker === "hotelInfoBackground"
                                  ? utils.colors.hotelInfoBackground ||
                                    "#f1f5f9"
                                  : showColorPicker === "dayBackground"
                                    ? utils.colors.dayBackground || "#f1f5f9"
                                    : showColorPicker === "dayBackgroundEnd"
                                      ? utils.colors.dayBackgroundEnd ||
                                        "#e2e8f0"
                                      : showColorPicker === "headerColor"
                                        ? utils.colors.headerColor || "#334155"
                                        : "#000000"
                  }
                  onChange={(e) => {
                    let hexValue = e.target.value;
                    // Add # if missing
                    if (hexValue && !hexValue.startsWith("#")) {
                      hexValue = "#" + hexValue;
                    }
                    // Validate hex format (3 or 6 characters after #)
                    if (
                      /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hexValue) ||
                      hexValue === "#"
                    ) {
                      onColorChange?.(showColorPicker, hexValue);
                    }
                  }}
                  placeholder="#000000"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                />
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default ModernTemplate;
